import { create, UseBoundStore, StoreApi } from 'zustand'
import { createDbgLogger } from 'utils/dbgLogger'
import waitPromise from 'utils/waitPromise'

const DEFAULT_RETRY_MS_DELAYS = [5000]

interface DependableResource<T> {
  value: T
  loaded: Loaded
  error: any | null

  /**
   * returns a Promise that waits for value to be resolved
   * if value is already loaded, will not trigger `getValue()` and resolve immediately
   */
  getLoadedValue: () => Promise<T>

  /**
   * mark loaded false and trigger to call `getValue()`
   */
  refresh: () => Promise<T>

  getIsLoading: () => boolean
}
export type Loaded = false | number
export type DependableResourceZustandStore<T> = UseBoundStore<StoreApi<DependableResource<T>>>

export type DependableResourceStore<T> = {
  (): DependableResource<T>
  <U>(selector: (state: DependableResource<T>) => U, equals?: (a: U, b: U) => boolean): U
  getState: DependableResourceZustandStore<T>['getState']
  start: () => void
  subscribe: DependableResourceZustandStore<T>['subscribe']
}

interface CreateDependableResourceParams<T> {
  initValue: T

  /**
   * produce `.value` of this resource
   */
  getValue: () => Promise<T> | T

  /**
   * what resources to depends on, will
   * * wait for these resources loaded to start loading
   * * trigger refreshing if these resources refreshes
   */
  dependResources?: DependableResourceStore<any>[]

  /**
   *
   * `onInit` runs on js code load to setup custom wiring, such as `onAccountInitOrChange` or redux
   * this function will be run with `{ getLoadedValue, refresh, setValue, resetValue, getIsStarted }` parameters
   */
  onInit?: (params: OnInitParams<T>) => any

  /**
   * array of ms for each retry, i.e. [5000, 10000] will retry after 5s after 1st failure, and 10s after 2nd failure
   * default: [5000], retry once after 5s
   */
  retryMsDelays?: number[]

  /**
   * enables `console.debug()` to log down more info, should be turned off after debugging
   */
  verbose?: boolean

  /**
   * name of this fresher to identify for debugging purpose
   */
  dbgName?: string
}

interface OnInitParams<T> {
  /**
   * returns a Promise that waits for value to be resolved
   * if value is already loaded, will not trigger `getValue()` and resolve immediately
   */
  getLoadedValue: () => Promise<T>

  /**
   * mark loaded false and trigger to call `getValue()`
   */
  refresh: () => Promise<T>

  /**
   * set value and mark loaded
   */
  setValue: (value: T) => void

  /**
   * reset value to initValue
   */
  resetValue: () => T

  /**
   * true if lazy started
   * can be used to prevent starting from custom events
   */
  getIsStarted: () => boolean

  getIsLoading: () => boolean
}

type AwaitingRefresh<T> = [resolve: (value: T) => void, reject: (error: any) => void]

const NOOP = () => undefined

/**
 * create a wrapped zustand store with value that needs to be fetched or computed as a resource
 * `getValue()` produce `.value`, which can then be obtained by:
 * * `useXXX(s => s.value)` as react hook, same as zustand (might not be loaded yet)
 * * `useXXX.getState().value` directly anywhere (might not be loaded yet)
 * * `useXXX.getState().getLoadedValue()` returns a Promise, resolve the value immediately if loaded already, otherwise resolve when loaded
 *
 * a resource is a singleton that prevents duplicated computation or fetching when its hook is invoked multiple times across components,
 * for example:
 *
 * ```
 * const useVvsPerBlock = createDependableResource<BigNumber>({
 *   initValue: new BigNumber(0),
 *   getValue: async () => {
 *     const craftsmanContract = getCraftsmanContract()
 *     return new BigNumber((await craftsmanContract.vvsPerBlock()).toString())
 *   },
 *   dependResources: [],
 *   dbgName: 'VvsPerBlock',
 *   // verbose: true,
 * })
 *
 * const App = () => {
 *   const vvsPerBlock = useVvsPerBlock(s => s.value)
 *   return <div><Component /> | { vvsPerBlock.toString() }</div>
 * }
 * const Component = () => {
 *   useVvsPerBlock(s => s.value)
 *   return (
 *     <button onClick={async () => {
 *       const vvsPerBlock = await useVvsPerBlock.getState().getLoadedValue()
 *       // vvsPerBlock is guaranteed to be loaded
 *     }}>DEMO</button>
 *   )
 * }
 * ```
 *
 * `getValue()` here runs once, which is suitable for fetching or expensive computation, also makes value stable for react dependencies
 *
 * ## `onInit`
 *
 * runs on js code load to setup custom wiring, such as `onAccountInitOrChange` or redux,
 * for example:
 *
 * ```
 * export const useUserMoleTokenIds = createDependableResource<string[]>({
 *   initValue: [],
 *   getValue: fetchMoleTokenIds,
 *   onInit: ({ resetValue, refresh, getIsStarted }) => {
 *     onAccountInitOrChange(() => {
 *       if (!getIsStarted()) return
 *       resetValue()
 *       refresh()
 *     })
 *   },
 *   dependResources: [],
 *   dbgName: 'UserMoleTokenIds',
 *   // verbose: true,
 * })
 * ```
 *
 * because `UserMoleTokenIds` is user/account/wallet related resource, need to refresh when change
 *
 * ## `dependResources`
 *
 * some FE data points are combination of multiple data sources,
 * for example:
 *
 * ```
 * export const useActiveOrUserBoostedFarms = createDependableResource<...>({
 *   initValue: []
 *   getValue: () => {
 *     const boostedFarms = useUserBoostedFarmsInfos.getState().value
 *     return useBoostableFarmsInfo.getState().value.filter(
 *       // using boostedFarms
 *     )
 *   },
 *   dependResources: [useBoostableFarmsInfo, useUserBoostedFarmsInfos],
 *   dbgName: 'ActiveOrUserBoostedFarms',
 *   // verbose: true,
 * })
 * ```
 *
 * when `useBoostableFarmsInfo` or `useUserBoostedFarmsInfos` change and loaded, `getValue()` will be triggered to refresh
 *
 * ## How to trigger refreshing after mutation?
 *
 * call `useXXX.getState().refresh()`, it returns a Promise that resolves from `getValue()`
 *
 * ## Lazy starting
 *
 * `getValue()` will start only when needed, i.e. hook or `.getState()...` invoked
 *
 * > if want to make resource load earlier, add the hook at page component level
 */
const createDependableResource = <T>({
  initValue,
  getValue,
  dependResources = [],
  onInit = () => undefined,
  retryMsDelays = DEFAULT_RETRY_MS_DELAYS,
  verbose,
  dbgName,
}: CreateDependableResourceParams<T>): DependableResourceStore<T> => {
  const logPrefix = dbgName ? `${dbgName} ` : ''
  const dbgLog = createDbgLogger(verbose)
  let started = false
  let loadedDependencies = 0
  let latestLoadId = 0
  let loading = 0
  let triggeredLoad: ReturnType<typeof setTimeout> = null
  const awaitingRefreshes: AwaitingRefresh<T>[] = []

  const zustandStore = create<DependableResource<T>>((set, get) => {
    const loadValue = async () => {
      const loadId = ++latestLoadId
      dbgLog(`${logPrefix}getValue... (#${loadId})`)

      loading += 1
      let retried = 0

      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          if (loadedDependencies < dependResources.length) {
            dbgLog(`${logPrefix}load stopped (#${loadId}) because dependResources not all ready`)
            break
          }

          // eslint-disable-next-line no-await-in-loop
          const value = await getValue()

          if (latestLoadId === loadId) {
            dbgLog(`${logPrefix}loaded (#${loadId}):`, value)
            set({ value, loaded: loadId, error: null })
          } else {
            dbgLog(`${logPrefix}loaded (#${loadId}) but detect newer loads, discard:`, value)
          }
          break
        } catch (error) {
          if (latestLoadId === loadId) {
            console.error(`${logPrefix}error (#${loadId}):`, error)
            set({ error })

            const nextMs = retryMsDelays[retried]
            if (!nextMs) break

            retried++
            console.warn(`${logPrefix}will retry #${retried} after ${nextMs}ms (load #${loadId})`)
            // eslint-disable-next-line no-await-in-loop
            await waitPromise(nextMs)
          } else {
            dbgLog(`${logPrefix}error (#${loadId}) but detect newer loads, ignored:`, error)
            break
          }
        }
      }

      loading -= 1
    }

    const refresh = () => {
      started = true
      set({ loaded: false })

      const promise = new Promise<T>((resolve, reject) => {
        awaitingRefreshes.push([resolve, reject])
      })

      if (loadedDependencies >= dependResources.length) {
        if (triggeredLoad) {
          clearTimeout(triggeredLoad)
        }
        triggeredLoad = setTimeout(loadValue, 0)
      }

      return promise
    }

    const getLoadedValue = async (): Promise<T> => {
      const { loaded, value } = get()
      if (loaded) return value
      return refresh()
    }

    const setValue = (value: T) => {
      started = true
      set({ value, loaded: ++latestLoadId, error: null })
    }

    const getIsLoading = () => loading > 0

    dbgLog(`${logPrefix}init...`)
    onInit({
      getLoadedValue,
      refresh,
      setValue,
      resetValue: () => {
        set({ value: initValue })
        return initValue
      },
      getIsLoading,
      getIsStarted: () => started,
    })

    return {
      value: initValue,
      loaded: false,
      error: null,
      getLoadedValue,
      refresh,
      getIsLoading,
    }
  })

  let startIfNot = () => {
    if (!started) {
      dbgLog(`${logPrefix}starting...`)

      dependResources.forEach((dependResource) => {
        if (dependResource.getState().loaded) {
          loadedDependencies++
        }
        subscribeToDependable(dependResource, (loaded) => {
          if (loaded) {
            loadedDependencies++
            zustandStore.getState().refresh()
          } else {
            loadedDependencies--
          }
        })
      })
      zustandStore.getState().refresh()
    }
    startIfNot = NOOP
    useDependableResource.getState = zustandStore.getState
    useDependableResource.start = NOOP
  }
  // @ts-ignore
  const useDependableResource: DependableResourceStore<T> = (...args) => {
    startIfNot()
    // @ts-ignore
    return zustandStore(...args)
  }
  useDependableResource.getState = (...args) => {
    startIfNot()
    return zustandStore.getState(...args)
  }
  useDependableResource.subscribe = zustandStore.subscribe
  useDependableResource.start = startIfNot

  subscribeToDependable(
    useDependableResource,
    (loaded, value) => {
      if (loaded) {
        while (awaitingRefreshes.length > 0) {
          awaitingRefreshes.shift()[0](value)
        }
      }
    },
    (error) => {
      while (awaitingRefreshes.length > 0) {
        awaitingRefreshes.shift()[1](error)
      }
    },
  )

  return useDependableResource
}

export default createDependableResource

export const subscribeToDependable = <T>(
  store: DependableResourceStore<T>,
  onLoad: (loaded: Loaded, value: T) => any,
  onError?: (error: any) => any,
) => {
  return store.subscribe((state, prevState) => {
    if (state.loaded !== prevState.loaded) {
      onLoad(state.loaded, state.value)
    } else if (onError && state.error !== prevState.error) {
      onError(state.error)
    }
  })
}
